import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  MinusCircleFilled,
} from '@ant-design/icons';
import { Button, Popover } from 'antd';
import { ReactNode } from 'react';

import {
  StopoverDataCheckCellType,
  StopoverDataCheckType,
  StopoverDataItemType,
} from '../../../../types';
import { createDateStringPtBr } from '../../../../utils/utils';

// Cores da planilha "Check-List - Programação Suape" do Simplício.
export const DATA_CHECK_CONFIG: Record<
  StopoverDataCheckType,
  { label: string; color: string; icon: ReactNode }
> = {
  P: {
    label: 'Presente',
    color: '#5B9E3F',
    icon: <CheckCircleFilled />,
  },
  A: {
    label: 'Ausente',
    color: '#FF9900',
    icon: <ExclamationCircleFilled />,
  },
  I: {
    label: 'Isento',
    color: '#4285F4',
    icon: <MinusCircleFilled />,
  },
};

const DATE_ITEMS: StopoverDataItemType[] = ['ETB', 'ETD'];

const AUTOMATIC_EXEMPTION_REASON: Partial<
  Record<StopoverDataItemType, string>
> = {
  TOWAGE: 'Marcado como "não existe" na manobra de atracação.',
  MOORING: 'Marcado como "não existe" na manobra de atracação.',
  PRODUCTS: 'Navio de apoio.',
  FINANCIAL_TABLES: 'Navio de apoio.',
  CARGO_MANIFEST: 'Navio de apoio.',
};

function formatValue(item: StopoverDataItemType, value: string | number) {
  if (DATE_ITEMS.includes(item)) return createDateStringPtBr(String(value));
  if (typeof value === 'number') return value.toLocaleString('pt-BR');
  return value;
}

type StopoverDataCheckCellProps = {
  item: StopoverDataItemType;
  label: string;
  check: StopoverDataCheckCellType;
  canEdit: boolean;
  isSaving: boolean;
  onExempt: () => void;
  onRemoveExemption: () => void;
};

export function StopoverDataCheckCell({
  item,
  label,
  check,
  canEdit,
  isSaving,
  onExempt,
  onRemoveExemption,
}: StopoverDataCheckCellProps) {
  const config = DATA_CHECK_CONFIG[check.status];

  function content() {
    if (check.status === 'P') {
      return (
        <div>{check.value !== null ? formatValue(item, check.value) : '-'}</div>
      );
    }
    if (check.status === 'A') {
      return (
        <div>
          <div>Não preenchido no PMIS ou ainda não recebido.</div>
          {canEdit && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              loading={isSaving}
              onClick={onExempt}
            >
              Marcar como isento
            </Button>
          )}
        </div>
      );
    }
    if (check.exemption) {
      return (
        <div>
          <div>
            Isento por {check.exemption.created_by || 'usuário'} em{' '}
            {createDateStringPtBr(check.exemption.created_at)}.
          </div>
          {canEdit && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              loading={isSaving}
              onClick={onRemoveExemption}
            >
              Remover isenção
            </Button>
          )}
        </div>
      );
    }
    return (
      <div>
        Isento automaticamente.{' '}
        {check.automatic_exemption && AUTOMATIC_EXEMPTION_REASON[item]}
      </div>
    );
  }

  return (
    <Popover title={`${label}: ${config.label}`} content={content()}>
      {/* O clique na linha abre o detalhe da escala; o da célula, não. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span
        style={{ color: config.color, fontSize: 18 }}
        onClick={(event) => event.stopPropagation()}
      >
        {config.icon}
      </span>
    </Popover>
  );
}

export function StopoverDataCheckLegend() {
  return (
    <div style={{ display: 'flex', gap: '16px', fontSize: 12 }}>
      {(['P', 'A', 'I'] as StopoverDataCheckType[]).map((status) => (
        <span key={status} style={{ display: 'flex', gap: '6px' }}>
          <span style={{ color: DATA_CHECK_CONFIG[status].color }}>
            {DATA_CHECK_CONFIG[status].icon}
          </span>
          {status} - {DATA_CHECK_CONFIG[status].label}
        </span>
      ))}
    </div>
  );
}
